/**
 * Show truncated ID with full value in a tooltip.
 */

import {toast} from '@wandb/weave/common/components/elements/Toast';
import {
  MOON_150,
  TEAL_500,
  TEAL_600,
} from '@wandb/weave/common/css/color.styles';
import {Icon} from '@wandb/weave/components/Icon';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import copyToClipboard from 'copy-to-clipboard';
import React, {useCallback} from 'react';
import styled from 'styled-components';

export const IdPanel = styled.div<{clickable?: boolean}>`
  padding: 0 4px;
  background-color: ${MOON_150};
  border-radius: 4px;
  margin-left: 4px;
  font-weight: 600;
  font-family: monospace;
  font-size: 10px;
  line-height: 20px;
  cursor: ${props => (props.clickable ? 'pointer' : 'default')};
`;
IdPanel.displayName = 'S.IdPanel';

const CopyableIdPanel = styled(IdPanel)`
  color: ${TEAL_600};
  &:hover {
    color: ${TEAL_500};
  }
`;
CopyableIdPanel.displayName = 'S.CopyableIdPanel';

const TooltipText = styled.span`
  white-space: nowrap;
`;
TooltipText.displayName = 'S.TooltipText';

const TooltipId = styled.span``;
TooltipId.displayName = 'S.TooltipId';

type IdProps = {
  id: string;

  type?: string;

  // This is useful for e.g. call ids in the grid where we want to add some highlighting
  // on parent mouseover.
  className?: string;
};

export const Id = ({id, type, className}: IdProps) => {
  const truncatedId = id.slice(-4);
  const trigger = <IdPanel className={className}>{truncatedId}</IdPanel>;
  const prefix = type ? `${type} ID` : 'ID';
  const content = (
    <TooltipText>
      {prefix}: <TooltipId>{id}</TooltipId>
    </TooltipText>
  );
  return <Tooltip trigger={trigger} content={content} />;
};

export const CopyableId = ({id, type, className}: IdProps) => {
  const truncatedId = id.slice(-4);
  const prefix = type ? `${type} ID` : 'ID';
  const onClick = useCallback(() => {
    copyToClipboard(id);
    toast(`${prefix} copied to clipboard`);
  }, [id, prefix]);
  const trigger = (
    <CopyableIdPanel clickable={true} className={className} onClick={onClick}>
      {truncatedId}
    </CopyableIdPanel>
  );
  const content = (
    <TooltipText>
      {prefix}: <TooltipId>{id}</TooltipId>
    </TooltipText>
  );
  return <Tooltip trigger={trigger} content={content} />;
};

type CopyableInlineIdProps = {
  id: string;
  label: string;
  toastMessage: string;
};

export const CopyableInlineId = ({
  id,
  label,
  toastMessage,
}: CopyableInlineIdProps) => {
  const onClick = useCallback(() => {
    copyToClipboard(id);
    toast(toastMessage);
  }, [id, toastMessage]);
  return (
    <button
      type="button"
      onClick={onClick}
      title="Click to copy"
      className="group flex min-w-0 items-center gap-3 text-left hover:text-moon-700">
      <span className="truncate">
        {label} {id}
      </span>
      <Icon
        name="copy"
        className="h-[0.75rem] w-[0.75rem] shrink-0 opacity-60 transition-opacity group-hover:opacity-100"
      />
    </button>
  );
};
